/**
 * ContactImporter - CSV import component for bulk importing contacts
 *
 * Allows users to upload a CSV file and import contacts into the organization directory.
 */

import { useState, useCallback, useRef } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';
import { clsx } from 'clsx';
import { Button } from '@components/ui/button';
import LoadingSpinner from '@components/ui/LoadingSpinner';
import {
  contactsApi,
  type ContactImportItem,
  type ContactBulkImportResponse,
} from '@services/api/contacts';

interface ContactImporterProps {
  organizationId: string;
  onImportComplete?: (result: ContactBulkImportResponse) => void;
}

interface ParsedCSV {
  headers: string[];
  rows: string[][];
  mappedContacts: ContactImportItem[];
}

// Common CSV column name mappings
const COLUMN_MAPPINGS: Record<string, keyof ContactImportItem> = {
  email: 'email',
  'e-mail': 'email',
  'email address': 'email',
  mail: 'email',
  name: 'name',
  'full name': 'name',
  'display name': 'name',
  'first name': 'name',
  firstname: 'name',
  company: 'company',
  organization: 'company',
  org: 'company',
  employer: 'company',
  phone: 'phone',
  telephone: 'phone',
  mobile: 'phone',
  cell: 'phone',
  'phone number': 'phone',
};

function parseCSV(content: string): ParsedCSV {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) {
    return { headers: [], rows: [], mappedContacts: [] };
  }

  // Parse header row
  const headerLine = lines[0];
  if (!headerLine) {
    return { headers: [], rows: [], mappedContacts: [] };
  }
  const headers = parseCSVLine(headerLine).map((h) => h.toLowerCase().trim());

  // Parse data rows
  const rows: string[][] = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const row = parseCSVLine(line);
    if (row.some((cell) => cell.trim())) {
      rows.push(row);
    }
  }

  // Map columns to contact fields
  const columnMap: Map<number, keyof ContactImportItem> = new Map();
  headers.forEach((header, index) => {
    const mappedField = COLUMN_MAPPINGS[header];
    if (mappedField) {
      columnMap.set(index, mappedField);
    }
  });

  // Convert rows to contacts
  const mappedContacts: ContactImportItem[] = rows.map((row) => {
    const contact: ContactImportItem = {};
    columnMap.forEach((field, colIndex) => {
      const value = row[colIndex]?.trim();
      if (value) {
        contact[field] = value;
      }
    });

    // If no name column was mapped but we have first/last name columns, try to combine them
    if (!contact.name) {
      const firstNameIdx = headers.findIndex((h) =>
        ['first name', 'firstname', 'first'].includes(h)
      );
      const lastNameIdx = headers.findIndex((h) =>
        ['last name', 'lastname', 'last', 'surname'].includes(h)
      );
      if (firstNameIdx >= 0 || lastNameIdx >= 0) {
        const firstName = row[firstNameIdx]?.trim() || '';
        const lastName = row[lastNameIdx]?.trim() || '';
        contact.name = [firstName, lastName].filter(Boolean).join(' ');
      }
    }

    return contact;
  });

  // Filter out contacts without email or name
  const validContacts = mappedContacts.filter((c) => c.email || c.name);

  return { headers, rows, mappedContacts: validContacts };
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        current += '"';
        i++; // Skip next quote
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
  }
  result.push(current);

  return result;
}

export function ContactImporter({
  organizationId,
  onImportComplete,
}: ContactImporterProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedCSV | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] =
    useState<ContactBulkImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File) => {
    setError(null);
    setImportResult(null);

    if (!file.name.toLowerCase().endsWith('.csv')) {
      setError('Please upload a CSV file');
      return;
    }

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = parseCSV(content);

        if (parsed.mappedContacts.length === 0) {
          setError(
            'No valid contacts found. Make sure your CSV has columns like "email", "name", "company", or "phone".'
          );
          setParsedData(null);
          return;
        }

        setParsedData(parsed);
      } catch {
        setError('Failed to parse CSV file');
        setParsedData(null);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFile(file);
      }
    },
    [handleFile]
  );

  const handleImport = async () => {
    if (!parsedData || parsedData.mappedContacts.length === 0) return;

    setIsImporting(true);
    setError(null);

    try {
      const result = await contactsApi.bulkImport({
        organizationId,
        contacts: parsedData.mappedContacts,
        skipDuplicates,
      });

      setImportResult(result);
      onImportComplete?.(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to import contacts'
      );
    } finally {
      setIsImporting(false);
    }
  };

  const handleReset = () => {
    setParsedData(null);
    setFileName(null);
    setImportResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Show import result
  if (importResult) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium text-neutral-900 dark:text-white">
            Import Complete
          </h4>
          <button
            onClick={handleReset}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Import More
          </button>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {importResult.imported}
            </div>
            <div className="text-xs text-green-700 dark:text-green-300">
              Imported
            </div>
          </div>
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {importResult.skipped}
            </div>
            <div className="text-xs text-yellow-700 dark:text-yellow-300">
              Skipped
            </div>
          </div>
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {importResult.failed}
            </div>
            <div className="text-xs text-red-700 dark:text-red-300">Failed</div>
          </div>
        </div>

        {importResult.results.some((r) => !r.success) && (
          <div className="mt-4">
            <h5 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Issues:
            </h5>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {importResult.results
                .filter((r) => !r.success)
                .slice(0, 10)
                .map((r, idx) => (
                  <div
                    key={idx}
                    className="text-xs text-neutral-600 dark:text-neutral-400 flex items-start gap-2"
                  >
                    <AlertCircle className="h-3 w-3 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span>
                      {r.email || r.name}: {r.error}
                    </span>
                  </div>
                ))}
              {importResult.results.filter((r) => !r.success).length > 10 && (
                <div className="text-xs text-neutral-500">
                  ...and{' '}
                  {importResult.results.filter((r) => !r.success).length - 10}{' '}
                  more
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Show preview and import button
  if (parsedData) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary-600" />
            <span className="text-sm font-medium text-neutral-900 dark:text-white">
              {fileName}
            </span>
          </div>
          <button
            onClick={handleReset}
            className="p-1 text-neutral-400 hover:text-neutral-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-3 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span className="text-sm text-neutral-700 dark:text-neutral-300">
              Found {parsedData.mappedContacts.length} contacts to import
            </span>
          </div>

          {/* Preview first few contacts */}
          <div className="mt-2 space-y-1">
            {parsedData.mappedContacts.slice(0, 3).map((contact, idx) => (
              <div
                key={idx}
                className="text-xs text-neutral-600 dark:text-neutral-400"
              >
                {contact.name || contact.email}
                {contact.name && contact.email && ` (${contact.email})`}
                {contact.company && ` - ${contact.company}`}
              </div>
            ))}
            {parsedData.mappedContacts.length > 3 && (
              <div className="text-xs text-neutral-500">
                ...and {parsedData.mappedContacts.length - 3} more
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="skipDuplicates"
            checked={skipDuplicates}
            onChange={(e) => setSkipDuplicates(e.target.checked)}
            className="h-4 w-4 text-primary-600 border-neutral-300 rounded"
          />
          <label
            htmlFor="skipDuplicates"
            className="text-sm text-neutral-700 dark:text-neutral-300"
          >
            Skip contacts with duplicate emails
          </label>
        </div>

        {error && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-red-600 dark:text-red-400">
              {error}
            </span>
          </div>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleImport}
            disabled={isImporting}
            className="flex-1"
          >
            {isImporting ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Importing...
              </>
            ) : (
              `Import ${parsedData.mappedContacts.length} Contacts`
            )}
          </Button>
          <Button variant="outline" onClick={handleReset} disabled={isImporting}>
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  // Show file upload area
  return (
    <div className="space-y-4">
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={clsx(
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
          isDragging
            ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20'
            : 'border-neutral-300 dark:border-neutral-600 hover:border-neutral-400'
        )}
      >
        <Upload className="mx-auto h-8 w-8 text-neutral-400" />
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          Drag & drop a CSV file or{' '}
          <label className="text-primary-600 hover:text-primary-700 cursor-pointer">
            browse
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
            />
          </label>
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          CSV with columns: email, name, company, phone
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
          <span className="text-sm text-red-600 dark:text-red-400">{error}</span>
        </div>
      )}

      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        <p className="font-medium mb-1">Supported column names:</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Email: email, e-mail, email address, mail</li>
          <li>Name: name, full name, display name, first name + last name</li>
          <li>Company: company, organization, org, employer</li>
          <li>Phone: phone, telephone, mobile, cell</li>
        </ul>
      </div>
    </div>
  );
}

export default ContactImporter;
